import React, { useEffect } from 'react';
import { colors } from '../styles/variables';
import CourseHeader from '../components/course/CourseHeader';
import CourseAbout from '../components/course/CourseAbout';
import CourseCurriculum from '../components/course/CourseCurriculum';
import CourseProjects from '../components/course/CourseProjects';
import CourseInstructors from '../components/course/CourseInstructors';
import CourseCta from '../components/course/CourseCta';
import CourseReviewSection from '../components/course/CourseReviewSection';
import Seo from '../components/common/Seo';
import { projectsByCourse } from '../data/projects';

const courseJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Course',
  name: 'C 프로그래밍',
  description: '시스템 프로그래밍의 기초, C 언어를 통한 실전 코딩 1:1 멘토링 과정.',
  provider: {
    '@type': 'EducationalOrganization',
    name: 'ElectroWave Club',
    sameAs: 'https://electrowave.kr',
  },
  inLanguage: 'ko',
  educationalLevel: 'beginner',
  url: 'https://electrowave.kr/c',
};

const CPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  // 코스 헤더 데이터
  const headerData = {
    tag: '시스템 프로그래밍',
    title: 'C 프로그래밍 과정',
    description: '프로그래밍의 기초가 되는 C 언어를 체계적으로 배우고 시스템 프로그래밍의 핵심 개념을 이해합니다. 메모리 관리, 포인터, 구조체 등 C의 강력한 기능을 실습 중심으로 학습합니다.',
    duration: '주 1~2시간 / 30주 과정',
    level: '입문자 대상',
    bgColor: colors.cColor,
    bgImage: '/assets/images/course-thumb-c.png'
  };
  
  // 코스 소개 데이터
  const aboutData = {
    subtitle: '컴퓨터의 작동 원리와 가장 가까운 C 언어를 배우며 프로그래밍의 기초를 다지는 과정입니다.',
    forWhom: [
      '프로그래밍을 처음 시작하는 입문자',
      '컴퓨터의 내부 동작 원리에 관심이 있는 분',
      '메모리 관리와 시스템 프로그래밍에 관심이 있는 분',
      '임베디드 시스템이나 하드웨어 제어에 관심이 있는 분',
      '다른 프로그래밍 언어의 기초가 되는 개념을 배우고 싶은 분'
    ],
    skills: [
      'C 언어 기본 문법과 프로그래밍 구조 이해',
      '포인터와 메모리 관리 능력',
      '자료구조 구현 및 활용 능력',
      '파일 입출력과 시스템 프로그래밍 기초',
      '효율적인 코드 작성과 디버깅 역량',
      '알고리즘 구현 및 문제 해결 능력'
    ],
    imageSrc: '/assets/images/course-thumb-c.png'
  };
  
  // 커리큘럼 데이터
  const curriculumData = {
    subtitle: 'PC의 구조부터 알고리즘 구현까지, C 언어를 체계적으로 배울 수 있는 단계별 커리큘럼입니다.',
    modules: [
      {
        title: '0. PC 구조, OS 특성 및 환경 분석, 개발환경 구축',
        description: '프로그래밍의 기초가 되는 컴퓨터 구조와 운영체제의 개념을 배우고 개발 환경을 구축합니다.',
        lessons: [
          'PC 구조 이해하기',
          'OS 특성 및 역할',
          'Windows, Linux, macOS에서의 개발 환경 설정',
          '컴파일러 설치 및 환경 변수 설정',
          '첫 번째 프로그램: Hello, World!'
        ]
      },
      {
        title: '1. C 프로그래밍 입문',
        description: 'C 언어의 기본 구조와 문법을 이해하고 간단한 프로그램을 작성합니다.',
        lessons: [
          'C 언어 소개 및 역사',
          '기본 구조: main() 함수와 헤더 파일',
          '주석과 코드 가독성',
          '기본 출력 함수: printf()',
          '컴파일 및 실행 과정 이해'
        ]
      },
      {
        title: '2. 자료형과 변수',
        description: 'C 언어의 다양한 자료형과 변수 선언 방법을 학습합니다.',
        lessons: [
          '기본 자료형: 정수형, 부동소수점형, 문자형',
          '변수 선언 및 초기화 방법',
          '상수의 선언 (const 키워드)',
          '형 변환과 데이터 손실',
          '오버플로우와 언더플로우'
        ]
      },
      {
        title: '3. 연산자와 표현식',
        description: 'C 언어의 다양한 연산자를 이해하고 활용합니다.',
        lessons: [
          '산술 연산자와 우선순위',
          '대입 연산자와 복합 대입 연산자',
          '증감 연산자의 전위와 후위 차이',
          '관계 연산자와 논리 연산자',
          '비트 연산자와 응용'
        ]
      },
      {
        title: '4. 제어문',
        description: '조건에 따른 프로그램 흐름 제어 방법을 학습합니다.',
        lessons: [
          'if, else if, else 문',
          '중첩 if 문과 블록 구조',
          'switch-case 구조와 활용',
          '삼항 연산자',
          '조건문을 활용한 프로그램 작성'
        ]
      }
    ]
  };
  
  // 프로젝트 데이터
  const projectsData = {
    subtitle: '수업에서 배운 내용을 바로 적용할 수 있는 실전형 C 프로젝트를 수행합니다.',
    projects: projectsByCourse.c,
  };
  
  // 강사진 데이터
  const instructorsData = {
    subtitle: 'C 프로그래밍과 시스템 프로그래밍 분야의 전문가들이 여러분의 성장을 돕습니다.',
    instructors: [
      {
        name: 'Rechard Park',
        title: '시스템 프로그래머',
        bio: '15년 이상의 임베디드 시스템 및 시스템 프로그래밍 경력을 보유한 전문가입니다. 운영체제 커널 개발, 디바이스 드라이버 작성 등의 분야에서 풍부한 경험을 가지고 있으며, C 언어의 실무 활용에 대한 깊이 있는 지식을 전달합니다.',
        image: '/assets/images/instructor-park-jiwon.png'
      }
    ]
  };
  
  // CTA 데이터
  const ctaData = {
    title: 'C 프로그래밍으로 시스템 개발의 기초를 다지세요',
    subtitle: 'ElectroWave Club의 C 프로그래밍 과정을 통해 시스템 프로그래밍의 핵심을 배우고 하드웨어 제어 역량을 갖춘 개발자로 성장하세요.',
    bgColor: colors.cColor,
    endColor: '#1B5E20'  // 진한 녹색
  };
  
  return (
    <>
      <Seo
        title="C 프로그래밍"
        description="시스템 프로그래밍의 기초, C 언어를 통한 실전 코딩 1:1 멘토링 과정."
        path="/c"
        ogImage="/assets/images/course-thumb-c.png"
        ogType="article"
        jsonLd={courseJsonLd}
      />
      <CourseHeader {...headerData} />
      <CourseAbout {...aboutData} />
      <CourseCurriculum {...curriculumData} />
      <CourseProjects {...projectsData} courseSlug="c" />
      <CourseInstructors {...instructorsData} />
      <CourseReviewSection courseTitle="C 프로그래밍" />
      <CourseCta {...ctaData} />
    </>
  );
};

export default CPage;
